import type { Announcement } from '../types';

const NEWS_READ_KEY_PREFIX = 'catalogofworks-news-read:';

export type NewsReadState = {
  lastReadAt: string;
  initialized: boolean;
  /**
   * Per-item open watermark: announcement id → ISO of the version that was opened.
   * If the announcement’s updated/sent time is later, treat as unread again.
   */
  readAtById: Record<string, string>;
};

function storageKey(tenantSlug: string): string {
  return `${NEWS_READ_KEY_PREFIX}${tenantSlug}`;
}

function normalizeReadAtById(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, string> = {};
  for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!id.trim() || typeof value !== 'string' || !value.trim()) continue;
    const ms = new Date(value).getTime();
    if (Number.isNaN(ms)) continue;
    out[id] = value;
  }
  return out;
}

/** Legacy `readIds: string[]` → map; use lastReadAt as the open watermark when present. */
function migrateLegacyReadIds(rawIds: unknown, lastReadAt: string): Record<string, string> {
  if (!Array.isArray(rawIds)) return {};
  const baseline = lastReadAt.trim() || new Date(0).toISOString();
  const out: Record<string, string> = {};
  for (const id of rawIds) {
    if (typeof id !== 'string' || !id.trim()) continue;
    out[id] = baseline;
  }
  return out;
}

/** Version clock for “did this news change since I opened it?” */
export function announcementVersionIso(
  announcement: Pick<Announcement, 'updatedAt' | 'sentAt' | 'createdAt'>,
): string | null {
  return announcement.updatedAt || announcement.sentAt || announcement.createdAt || null;
}

export function isAnnouncementListUnread(
  announcement: Pick<Announcement, 'id' | 'updatedAt' | 'sentAt' | 'createdAt'>,
  readAtById: Record<string, string>,
): boolean {
  const openedAt = readAtById[announcement.id];
  if (!openedAt) return true;
  const version = announcementVersionIso(announcement);
  if (!version) return false;
  const openedMs = new Date(openedAt).getTime();
  const versionMs = new Date(version).getTime();
  if (Number.isNaN(openedMs) || Number.isNaN(versionMs)) return true;
  return versionMs > openedMs;
}

export function loadNewsReadState(tenantSlug: string): NewsReadState | null {
  if (!tenantSlug) return null;
  try {
    const raw = localStorage.getItem(storageKey(tenantSlug));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<NewsReadState> & { readIds?: unknown };
    if (typeof parsed.lastReadAt !== 'string') return null;

    const fromMap = normalizeReadAtById(parsed.readAtById);
    const fromLegacy = migrateLegacyReadIds(parsed.readIds, parsed.lastReadAt);
    const readAtById = { ...fromLegacy, ...fromMap };

    return {
      lastReadAt: parsed.lastReadAt,
      initialized: parsed.initialized === true,
      readAtById,
    };
  } catch {
    return null;
  }
}

export function saveNewsReadState(tenantSlug: string, state: NewsReadState): void {
  if (!tenantSlug) return;
  localStorage.setItem(
    storageKey(tenantSlug),
    JSON.stringify({
      lastReadAt: state.lastReadAt,
      initialized: state.initialized,
      readAtById: normalizeReadAtById(state.readAtById),
    }),
  );
}

/** Baseline / “list seen” watermark — keeps per-item open times. */
export function markNewsRead(tenantSlug: string, readAtIso: string): void {
  const prev = loadNewsReadState(tenantSlug);
  saveNewsReadState(tenantSlug, {
    lastReadAt: readAtIso,
    initialized: true,
    readAtById: prev?.readAtById ?? {},
  });
}

/** Guest opened a single announcement detail at this content version. */
export function markNewsItemRead(
  tenantSlug: string,
  announcementId: string,
  versionIso: string,
): Record<string, string> {
  const id = String(announcementId || '').trim();
  if (!tenantSlug || !id) return loadNewsReadState(tenantSlug)?.readAtById ?? {};

  const prev = loadNewsReadState(tenantSlug);
  const stamp = String(versionIso || '').trim() || new Date().toISOString();
  const readAtById = {
    ...(prev?.readAtById ?? {}),
    [id]: stamp,
  };
  saveNewsReadState(tenantSlug, {
    lastReadAt: prev?.lastReadAt || stamp,
    initialized: prev?.initialized ?? true,
    readAtById,
  });
  return readAtById;
}
